import {
  type FlightLeg,
  type ImportBatch,
  type LegIssue,
  type ScheduleProvider,
  type ScheduleSnapshot,
} from './types'

/**
 * The schedule with no server behind it.
 *
 * `sessionStorage`, like the reference overlay and for the same reason
 * (CODE-RULES 2): OMC workstations are shared, and one shift must not inherit
 * the previous shift's uncommitted import. A tab is therefore the honest
 * lifetime for data that has nowhere durable to go until the Supabase provider
 * lands.
 *
 * Two things follow from the size of a month of flying — 12.000 legs from one
 * report:
 *
 * 1. **Rows are stored as tuples, not objects.** The field names are half of a
 *    JSON object and would be repeated 12.000 times; a month fits in about a
 *    megabyte this way and does not fit comfortably otherwise.
 * 2. **Storage is best-effort.** When the quota is refused the board keeps
 *    working from memory for this page view and says so, rather than losing
 *    an import that has already been reviewed and confirmed.
 */

const LEGS_KEY = 'omc.schedule.legs.v2'
const BATCHES_KEY = 'omc.schedule.batches.v1'
const UNDO_KEY = 'omc.schedule.undo.v1'

/** Minute resolution: no ops file carries seconds, and it halves the digits. */
const MINUTE = 60_000

type Encoded = [
  operatingDate: string,
  flightNumber: string,
  serviceType: string,
  regn: string,
  acType: string,
  dep: string,
  arr: string,
  stdMinutes: number,
  staOffset: number,
  etdOffset: number | null,
  etaOffset: number | null,
  blockMinutes: number,
  batchIndex: number,
  cancelledAt: string | null,
  issues: LegIssue[],
  /** Station-local readings as filed: STD, STA, ETD, ETA. */
  localTimes: [std: string, sta: string, etd: string, eta: string],
]

interface UndoRecord {
  /** Keys the import created, to be dropped again. */
  dropped: string[]
  /** Rows as they were before the import, to be put back. */
  restore: Encoded[]
  batches: number
}

function encode(leg: FlightLeg, batchIndex: number): Encoded {
  return [
    leg.operatingDate,
    leg.flightNumber,
    leg.serviceType,
    leg.regn,
    leg.acType,
    leg.dep,
    leg.arr,
    Math.round(leg.stdUtc / MINUTE),
    Math.round((leg.staUtc - leg.stdUtc) / MINUTE),
    leg.etdUtc === null ? null : Math.round((leg.etdUtc - leg.stdUtc) / MINUTE),
    leg.etaUtc === null ? null : Math.round((leg.etaUtc - leg.staUtc) / MINUTE),
    leg.blockMinutes,
    batchIndex,
    leg.cancelledAt,
    leg.issues,
    [leg.stdLocal, leg.staLocal, leg.etdLocal, leg.etaLocal],
  ]
}

function decode(row: Encoded, batchIds: string[]): FlightLeg {
  const stdUtc = row[7] * MINUTE
  const staUtc = stdUtc + row[8] * MINUTE
  return {
    key: `${row[0]}|${row[1]}|${row[5]}|${row[6]}`,
    operatingDate: row[0],
    flightNumber: row[1],
    serviceType: row[2],
    regn: row[3],
    acType: row[4],
    dep: row[5],
    arr: row[6],
    stdUtc,
    staUtc,
    etdUtc: row[9] === null ? null : stdUtc + row[9] * MINUTE,
    etaUtc: row[10] === null ? null : staUtc + row[10] * MINUTE,
    blockMinutes: row[11],
    batchId: batchIds[row[12]] ?? '',
    cancelledAt: row[13],
    issues: row[14] ?? [],
    stdLocal: row[15]?.[0] ?? '',
    staLocal: row[15]?.[1] ?? '',
    etdLocal: row[15]?.[2] ?? '',
    etaLocal: row[15]?.[3] ?? '',
  }
}

/** Every access guarded: storage throws outright in private mode. */
function readJson<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    return null
  }
}

export class LocalScheduleProvider implements ScheduleProvider {
  readonly name = 'local'

  /** Authoritative for this page view; storage is a copy of it. */
  private legs: FlightLeg[] | null = null
  private batches: ImportBatch[] = []
  private undoRecord: UndoRecord | null = null
  private persisted = true

  async load(): Promise<ScheduleSnapshot> {
    if (this.legs === null) {
      this.batches = readJson<ImportBatch[]>(BATCHES_KEY) ?? []
      const rows = readJson<Encoded[]>(LEGS_KEY) ?? []
      const ids = this.batches.map((batch) => batch.id)
      this.legs = rows.map((row) => decode(row, ids))
      this.undoRecord = readJson<UndoRecord>(UNDO_KEY)
    }
    return this.snapshot()
  }

  async apply(legs: FlightLeg[], batch: ImportBatch): Promise<ScheduleSnapshot> {
    await this.load()
    const before = new Map((this.legs ?? []).map((leg) => [leg.key, leg]))
    const batchIds = [...this.batches.map((entry) => entry.id), batch.id]

    // The inverse of the import, not a second copy of the schedule: the keys
    // it created and the rows it overwrote are a fraction of the size and
    // restore the same state.
    const dropped: string[] = []
    const restore: Encoded[] = []
    for (const leg of legs) {
      const previous = before.get(leg.key)
      if (!previous) {
        dropped.push(leg.key)
      } else if (JSON.stringify(previous) !== JSON.stringify(leg)) {
        restore.push(encode(previous, Math.max(0, batchIds.indexOf(previous.batchId))))
      }
    }

    this.legs = legs
    this.batches = [...this.batches, batch]
    this.undoRecord = { dropped, restore, batches: this.batches.length - 1 }
    this.write()
    return this.snapshot()
  }

  async undo(): Promise<ScheduleSnapshot> {
    await this.load()
    const record = this.undoRecord
    if (!record || this.legs === null) return this.snapshot()

    const dropped = new Set(record.dropped)
    const ids = this.batches.map((batch) => batch.id)
    const restored = new Map(
      record.restore.map((row) => {
        const leg = decode(row, ids)
        return [leg.key, leg] as const
      }),
    )

    this.legs = this.legs
      .filter((leg) => !dropped.has(leg.key))
      .map((leg) => restored.get(leg.key) ?? leg)
    this.batches = this.batches.slice(0, record.batches)
    this.undoRecord = null
    this.write()
    return this.snapshot()
  }

  async clear(): Promise<ScheduleSnapshot> {
    this.legs = []
    this.batches = []
    this.undoRecord = null
    this.write()
    return this.snapshot()
  }

  private write(): void {
    const ids = this.batches.map((batch) => batch.id)
    try {
      sessionStorage.setItem(
        LEGS_KEY,
        JSON.stringify((this.legs ?? []).map((leg) => encode(leg, ids.indexOf(leg.batchId)))),
      )
      sessionStorage.setItem(BATCHES_KEY, JSON.stringify(this.batches))
      if (this.undoRecord) sessionStorage.setItem(UNDO_KEY, JSON.stringify(this.undoRecord))
      else sessionStorage.removeItem(UNDO_KEY)
      this.persisted = true
    } catch {
      // Quota, or private mode. The import stays live in memory for this page
      // view; what is lost is only its survival across a reload.
      this.persisted = false
      try {
        sessionStorage.removeItem(LEGS_KEY)
        sessionStorage.removeItem(UNDO_KEY)
      } catch {
        // Nothing was stored in the first place.
      }
    }
  }

  private snapshot(): ScheduleSnapshot {
    return {
      legs: this.legs ?? [],
      batches: this.batches,
      persisted: this.persisted,
      canUndo: this.undoRecord !== null,
    }
  }
}
